/**
 * useHeaderCollapse — fold and then scroll away the pinned top block, tied to
 * the finger rather than switched by it.
 *
 * WHY. The gym shell pins a tall block to the top: the gym header, the tab bar
 * and, on the map, the search row. Sticky and always visible, that eats a
 * quarter of a phone screen from every list. Un-sticking it is worse — the
 * tabs would only be reachable from the very top of a long routes list.
 *
 * The first attempt was a boolean: past a threshold hide, on any upward
 * movement show. It worked, but a whole bar vanishing in one step is a jolt in
 * the corner of the eye, and it is not what the page is doing — the page is
 * being dragged, so the block is dragged too.
 *
 * TWO STAGES, AND THEY ARE DRIVEN BY DIFFERENT THINGS. That split is the whole
 * design, and it is deliberate:
 *
 *   labels → TRAVEL DOWN, with position as a floor. They fold over the first
 *     COLLAPSE_LABEL_PX the reader travels DOWNWARD, and upward travel never
 *     buys them back — only actually returning to the top of the page does,
 *     over the same stretch. So they still never slide in and out mid-list,
 *     but an ARRIVAL starts with them open wherever it lands.
 *   block  → DIRECTION, and on the way back, SPEED. Down it slides away pixel
 *     for pixel over COLLAPSE_SLIDE_PX. Up it waits for a FAST movement: the
 *     upward intent leaks at COLLAPSE_INTENT_LEAK px/ms, so only travel that
 *     arrives faster than that can reach COLLAPSE_REVEAL_PX. So the bar
 *     returns wherever you are — but only when you ask for it.
 *
 * Which is what a reader wants from each: navigation should be one upward
 * flick away anywhere in a long list, but the labels are a heavier thing to
 * put back on screen, and having them slide in and out under a slow thumb
 * halfway down a list is noise. Scrolling slowly all the way back to the top
 * therefore brings back the icons-only bar the whole way, and the labels only
 * as you arrive.
 *
 * Notes:
 *
 * - Nothing is unmounted and nothing leaves the layout: the block moves by
 *   transform, so content below never reflows, and the four surfaces that
 *   measure this block (the admin scrim, the settings drawer, the competition
 *   card focus offset, the event list) still read its real height.
 * - The slide budget only starts once the labels are gone, so the two stages
 *   run in order rather than at once. Crossing into it starts from zero
 *   travel, not from the offset itself — otherwise the block would lurch on
 *   the pixel where the fold ends. Neither budget spends the first
 *   COLLAPSE_TOP_PX of the page: that stretch is the block's resting zone.
 * - The upward intent leaks rather than accumulating: a distance-only
 *   threshold cannot tell a reader nudging their way back up a page from one
 *   asking for the menu, because over enough time both add up to the same
 *   pixels. Time is the only thing that separates them. The leak was tuned up
 *   from 0.35 px/ms once it was in the hand: at that rate an ordinary brisk
 *   scroll back up the page (~600 px/s) brought the bar out after ~280px,
 *   which is not a request for it. At 0.5 the same speed goes ~850px
 *   untouched, while a real flick still lands inside ~150ms.
 * - `frozen` holds the block exactly where it is and stops listening. The
 *   profile menu opens as a panel hanging off its chip in the header, and a
 *   header that then slides out from under it leaves the menu floating in the
 *   middle of the page, attached to nothing. Freezing is deliberately not the
 *   same as "open": snapping the bar wide the moment a menu opens is its own
 *   jolt, and the reader did not ask for it. On unfreezing, the reference
 *   offset is re-synced rather than read as travel — the page may have moved
 *   while the menu was up, and spending that as travel would slam the block
 *   shut the instant it closes.
 * - `isProgrammatic` is how the block sits out scrolling it did not cause.
 *   `useScrollMemory` does not jump once and stop: it re-applies the tab's
 *   remembered offset EVERY FRAME for up to 1.5s while the page fills in. Read
 *   as travel, that stream spends the budget in whichever direction the
 *   restore happens to be going, so on a tab switch the bar popped open (the
 *   reset), then slid out again under the restore, then came back as the
 *   position cap caught up — the whole top bar flickering, worst on a phone
 *   where the content streams in slowest. Those samples still move the
 *   reference; they move neither the slide nor the labels, because the
 *   destination is already known and adopted. They just do not buy or
 *   refund any of the slide. `pendingScrollY` completes it: the reset adopts
 *   the offset the restore is heading FOR — read straight out of storage, so
 *   it is available during the render itself — and the first painted frame is
 *   already the shape the tab will settle into. If the restore then falls
 *   short (a tab whose content shrank), the settle pass takes the page's real
 *   offset once the restore lets go.
 * - `resetKey` gives the block back WHOLE on a view change — slide budget and
 *   labels both. Two reasons, and the second one took the longest to see.
 *   Scroll position is also set programmatically (`useScrollMemory` restores
 *   each tab's own offset on a switch), and that jump would otherwise be spent
 *   as slide budget, hiding the bar exactly as the reader arrives. And the
 *   labels used to follow the restored POSITION, which meant a switch between
 *   a tab at the top and one parked part way down CHANGED THE HEIGHT OF THE
 *   BLOCK at the moment of the switch. That is what was being reported as the
 *   whole top bar blinking — and it was a difference only those pairs had,
 *   which is exactly the pairs it was seen on. Every switch now arrives at the
 *   same shape, so there is nothing left to differ.
 *
 * This is deliberate behaviour, not the webview's. An installed PWA has been
 * seen doing something that resembles it, and then not doing it after a
 * restart, because a fixed or sticky element is anchored to a layout viewport
 * that momentarily disagrees with the screen. That is luck; this is not.
 */
import { useEffect, useRef, useState } from "react";

export const COLLAPSE_TOP_PX = 24; // above this the block is always fully open
export const COLLAPSE_LABEL_PX = 48; // ...then this much of the page folds the labels
export const COLLAPSE_SLIDE_PX = 72; // ...and this much of TRAVEL slides the block away
export const COLLAPSE_REVEAL_PX = 150; // upward travel that has to build up FASTER than it leaks
export const COLLAPSE_INTENT_LEAK = 0.5; // px per ms the upward intent drains away
export const COLLAPSE_FOLD_END_PX = COLLAPSE_TOP_PX + COLLAPSE_LABEL_PX;
// How long to wait for a scroll restore to finish before taking the page's
// real offset. Comfortably past useScrollMemory's own 1.5s budget.
const SETTLE_TIMEOUT_MS = 2500;
const SETTLE_POLL_MS = 120;

const clamp01 = v => (v < 0 ? 0 : v > 1 ? 1 : v);

/**
 * The two stages. Note the different inputs — that is the point.
 *
 * The slide also has a POSITION CAP: it can never be more hidden than the page
 * position alone would justify. Without it, a reader who crept back up without
 * ever asking for the block (no fast movement, so no intent) kept a fully
 * hidden bar all the way to the top, where the budget reset dropped it back in
 * one frame — arriving late and as a jump. With the cap the block is drawn
 * back in over the last `COLLAPSE_SLIDE_PX` before the fold zone, so it is
 * already home by the time the page is. On the way DOWN the cap never binds:
 * leaving the fold zone, budget and cap grow at exactly the same rate.
 *
 * The labels take the MORE OPEN of their two rules — the travel budget and
 * the old position rule. The position rule is what brings them back as the
 * reader arrives at the top; the budget is what keeps an arrival from landing
 * with them already folded.
 *
 * @param {number} scrollY   - where the page is (floors the labels, caps the slide)
 * @param {number} progress  - px of slide budget spent (drives the block)
 * @param {number} labels    - px of downward travel spent folding the labels
 * @returns {{labelOpen: number, shift: number}} both 0…1: how much of the
 *   label still shows, and how far the block has slid out (1 = gone).
 */
/**
 * Where the pinned block's visible bottom edge is, in px from the top of the
 * screen: what a `position: sticky` header of the page content must use as its
 * `top` (published as `--sendly-sticky-top`). The block is `position: fixed`,
 * so a `top: 0` sticky element sticks BEHIND it. Never above the iOS
 * status-bar strip, which stays painted when the block has slid out.
 *
 * @param {number} headerHeight - the block's measured height (inset included)
 * @param {number} shift        - 0…1, how far the block has slid out
 * @param {number} inset        - the iOS status-bar strip's height (0 elsewhere)
 */
export function stickyTopPx(headerHeight, shift, inset = 0) {
  const h = Number.isFinite(headerHeight) && headerHeight > 0 ? headerHeight : 0;
  const s = clamp01(Number.isFinite(shift) ? shift : 0);
  const floor = Number.isFinite(inset) && inset > 0 ? inset : 0;
  return Math.max(floor, Math.round(h * (1 - s)));
}

export function collapseStages(scrollY, progress, labels = 0) {
  const byPosition = clamp01((scrollY - COLLAPSE_FOLD_END_PX) / COLLAPSE_SLIDE_PX);
  return {
    labelOpen: Math.max(
      1 - clamp01(labels / COLLAPSE_LABEL_PX),
      1 - clamp01((scrollY - COLLAPSE_TOP_PX) / COLLAPSE_LABEL_PX),
    ),
    shift: Math.min(clamp01(progress / COLLAPSE_SLIDE_PX), byPosition),
  };
}

/**
 * One scroll sample. Pure, so the rule is testable without a scrolling
 * browser. State is `{progress, up, revealing, t}`: how much of the block has
 * slid away, how much upward intent has built up, whether that intent has
 * been established, and when the last sample was.
 *
 * The two directions are deliberately not symmetric:
 *
 * - Going DOWN it slides immediately, pixel for pixel. Getting out of the way
 *   is the thing being asked for, and making the reader earn it would feel
 *   sticky.
 * - Going UP it takes a FAST movement, not merely a long one. This is the
 *   distinction that matters and the one that took a couple of tries to get
 *   right: a plain distance threshold cannot tell "I nudged the wheel up
 *   twenty times over a minute, reading" from "I flicked up because I want
 *   the menu" — both add up to the same pixels. So the intent LEAKS: it
 *   drains at `leak` px per ms, and only movement that arrives faster than
 *   that can pile up to `reveal`. Wheel notches spaced out while reading
 *   evaporate between notches; four in quick succession, or one flick, do
 *   not. The reader can therefore go up and down a page all day and be left
 *   alone, and still get navigation the moment they ask for it.
 *
 * Once the intent lands, `revealing` LATCHES until the next downward movement,
 * so the block keeps following the finger even if the reader slows down
 * mid-way — otherwise it would stall half-open the moment the leak caught up.
 */
export function nextCollapseState(state, y, lastY, now, opts = {}) {
  const {
    slide = COLLAPSE_SLIDE_PX,
    reveal = COLLAPSE_REVEAL_PX,
    leak = COLLAPSE_INTENT_LEAK,
    label = COLLAPSE_LABEL_PX,
    top = COLLAPSE_TOP_PX,
  } = opts;
  const labels = state.labels || 0;
  // Home. Both budgets go back, so travelling down from here folds the labels
  // over their full stretch again rather than from wherever they were left.
  if (y <= top) return { progress: 0, labels: 0, up: 0, revealing: false, t: now };
  const moved = y - lastY;
  const drained = Math.max(0, state.up - leak * Math.max(0, now - state.t));
  if (moved === 0) return { ...state, labels, up: drained, t: now };
  if (moved > 0) {
    // Down: the labels fold first and the block only starts sliding once they
    // are gone, so the two stages run in order rather than both at once on the
    // first flick. Give up on whatever upward intent had built up.
    //
    // Only the part of the movement ABOVE `top` counts. The first stretch of
    // the page is the block's own resting zone; spending it would start
    // folding the labels on the very first pixel of a scroll from the top.
    const spend = Math.min(moved, Math.max(0, y - top));
    const toLabels = Math.min(Math.max(0, label - labels), spend);
    return {
      labels: labels + toLabels,
      progress: Math.min(slide, state.progress + (spend - toLabels)),
      up: 0, revealing: false, t: now,
    };
  }
  const up = drained - moved; // moved is negative
  const revealing = state.revealing || up > reveal;
  return {
    // The labels are NOT refunded going up: having them slide back in under a
    // slow thumb halfway down a list is exactly the noise this design avoids.
    // Only arriving at the top brings them back.
    labels,
    progress: revealing ? Math.max(0, state.progress + moved) : state.progress,
    up, revealing, t: now,
  };
}

const now = () => (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());

/** Current offset, with iOS rubber-band overscroll clamped away. */
function readScrollY() {
  if (typeof window === "undefined") return 0;
  const y = Math.max(window.scrollY || 0, 0);
  const max = (document.documentElement?.scrollHeight || 0) - (window.innerHeight || 0);
  // Overscrolling past the end spends budget and the bounce gives it straight
  // back — the block would twitch at the bottom edge.
  return max > 0 ? Math.min(y, max) : y;
}

/** Where an arriving view starts: its remembered offset if there is one. */
function openingOffset(pendingScrollY) {
  const heading = pendingScrollY?.();
  return typeof heading === "number" ? heading : readScrollY();
}

/**
 * @param {object} [options]
 * @param {any}     [options.resetKey] - changing this gives the block back (view change)
 * @param {boolean} [options.frozen]   - hold it where it is and stop listening
 * @param {function} [options.isProgrammatic] - () => true while something else
 *   is driving the scroll position; those samples are sat out entirely
 * @param {function} [options.pendingScrollY] - () => the offset the incoming
 *   view is about to be restored to, or null/undefined for "wherever the page
 *   is". Must be answerable during a render (see `peekScrollMemory`).
 * @returns {{scrollY: number, progress: number, labelOpen: number, shift: number}}
 */
export function useHeaderCollapse({ resetKey, frozen = false, isProgrammatic, pendingScrollY } = {}) {
  const programmatic = useRef(isProgrammatic);
  programmatic.current = isProgrammatic;
  const pending = useRef(pendingScrollY);
  pending.current = pendingScrollY;
  // The reset key is carried INSIDE the state, so a render can see for itself
  // that the state belongs to the view we just left — see the adjustment below.
  // The first mount is a view change too: a reload lands on a remembered
  // offset just as a switch does.
  const [state, setState] = useState(() => ({ key: resetKey, y: Math.round(openingOffset(pendingScrollY)), progress: 0, labels: 0 }));
  // Seeded from the opening offset, not from 0: a first scroll before the
  // settle pass would otherwise read as one huge downward delta.
  const lastY = useRef(state.y);
  // The budget lives in a ref, not in the state updater. A function updater
  // runs during React's render phase, i.e. AFTER the scroll callback has
  // finished — so a `lastY.current = y` at the end of the callback would
  // already have landed, every delta would come out 0, and nothing would ever
  // move. Both are stepped here, in the callback, and the state is only a
  // mirror for rendering.
  const budget = useRef({ progress: 0, labels: 0, up: 0, revealing: false, t: 0 });
  const everListened = useRef(false);

  // THE RESET HAPPENS DURING RENDER, not in an effect — the documented
  // "adjust state when a prop changes" pattern. It has to, and the reason is
  // the whole history of this bug.
  //
  // The tab switch is dispatched inside `startTransition` (see `TabBar` in
  // ui.js), so its commit is a transition commit. A `setState` made from an
  // effect during such a commit is NOT guaranteed to be flushed before the
  // browser paints — even from a layout effect. When it is not, the first
  // painted frame of the switch shows the OUTGOING tab's collapse state, and
  // the correction lands one frame later: the whole top block blinks. Only on
  // tabs remembered part way down the page, because those are exactly the ones
  // whose collapse state differs from the tab being left — which is precisely
  // what was reported, in both directions.
  //
  // Adjusting the state here removes the timing question entirely: React
  // re-runs this component immediately and only then commits, so no stale
  // shape can reach the screen in any mode.
  //
  // It also adopts the offset the incoming view is HEADING FOR rather than the
  // one the page is at. `pendingScrollY` reads that straight out of storage,
  // which works during a render; the restore ref does not, because it is only
  // filled in the layout phase, after this. While the page fills in the
  // restore can take many frames to arrive, and following it there would fold
  // the labels open and shut on the way.
  if (state.key !== resetKey) {
    const y = openingOffset(pending.current);
    lastY.current = y;
    budget.current = { progress: 0, labels: 0, up: 0, revealing: false, t: now() };
    setState({ key: resetKey, y: Math.round(y), progress: 0, labels: 0 });
  }

  // ...and the guess is settled once the restore lets go. A remembered offset
  // is a bet on a page that is still filling in, and a tab whose content
  // shrank never reaches it — leaving the block folded for an offset the page
  // is not at, with no scroll event coming to correct it. Taken as position,
  // never as travel: the reader did not scroll here.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    let timer = 0;
    const deadline = now() + SETTLE_TIMEOUT_MS;
    const check = () => {
      // Polled, not per-frame: this is waiting on a process measured in
      // hundreds of milliseconds, and nothing here needs frame precision.
      if (programmatic.current?.() && now() < deadline) { timer = setTimeout(check, SETTLE_POLL_MS); return; }
      const y = readScrollY();
      lastY.current = y;
      const rounded = Math.round(y);
      setState(prev => (prev.y === rounded ? prev : { ...prev, y: rounded }));
    };
    timer = setTimeout(check, SETTLE_POLL_MS);
    return () => clearTimeout(timer);
  }, [resetKey]);

  useEffect(() => {
    if (frozen || typeof window === "undefined") return;
    // Picking the reference back up, not reading it as movement: whatever the
    // page did while frozen is not something the reader asked the block to do.
    // Only on an UNfreeze, though — on the first run there is nothing to pick
    // back up, and the page has not caught up with the opening offset yet, so
    // syncing here would throw that offset away before it is ever used.
    if (everListened.current) {
      lastY.current = readScrollY();
      budget.current = { ...budget.current, t: now() };
      setState(prev => (prev.y === Math.round(lastY.current) ? prev : { ...prev, y: Math.round(lastY.current) }));
    }
    everListened.current = true;
    let queued = false;
    const read = () => {
      queued = false;
      // A modal is open: `ScrollLock` pins the body (`position: fixed`) and
      // parks the window at 0. That 0 is chrome state, not the reader's place
      // in the page — spent as travel it throws the block wide open behind the
      // modal, and the block is `position: fixed`, so unlike the old sticky one
      // it is genuinely on screen when that happens. Skipping entirely also
      // leaves the reference untouched, so the offset ScrollLock puts back on
      // close arrives as a delta of zero.
      if (document.body.style.position === "fixed") return;
      const y = readScrollY();
      // Rounded to whole px: these values drive a React render, and a stream
      // of sub-pixel differences would re-render without moving anything.
      // Somebody else is moving the page: keep the reference in step and
      // change nothing else. Not the budget, and not the position either —
      // the restore climbs towards its target over as many frames as the page
      // needs to grow, and following it would fold the labels shut and open
      // again on the way. The reset already adopted the destination.
      if (programmatic.current?.()) {
        budget.current = { ...budget.current, up: 0, revealing: false, t: now() };
        lastY.current = y;
        return;
      }
      const next = nextCollapseState(budget.current, y, lastY.current, now());
      budget.current = next;
      lastY.current = y;
      const rounded = Math.round(y), progress = Math.round(next.progress), labels = Math.round(next.labels);
      // Spread, so the reset key rides along: dropping it would make the very
      // next render look like a view change and throw the block back open.
      setState(prev => (prev.y === rounded && prev.progress === progress && prev.labels === labels)
        ? prev : { ...prev, y: rounded, progress, labels });
    };
    const onScroll = () => {
      if (queued) return;
      queued = true;
      if (typeof requestAnimationFrame === "function") requestAnimationFrame(read);
      else read();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [frozen]);

  return { scrollY: state.y, progress: state.progress, ...collapseStages(state.y, state.progress, state.labels) };
}
